import React from 'react';
import { Icon, Stack, Text, TooltipHost } from '@fluentui/react';
import { ITheme } from '@fluentui/react';
import { SreInflightSummary } from '../../../../../AGSS/models/SreHealth';

interface ISreTriageSummaryProps {
    summary: SreInflightSummary;
    theme: ITheme;
    hasActiveFilter: boolean;
    activeAgeFilterHours?: number;
    onFilterByAge: (hours: number | undefined) => void;
}

interface ChipProps {
    label: string;
    value: number | string;
    theme: ITheme;
    tone?: 'neutral' | 'warning' | 'danger' | 'primary';
    tooltip?: string;
    isActive?: boolean;
    onClick?: () => void;
}

const Chip: React.FC<ChipProps> = ({
    label,
    value,
    theme,
    tone = 'neutral',
    tooltip,
    isActive,
    onClick,
}) => {
    const toneColor = {
        neutral: theme.palette.neutralPrimary,
        primary: theme.palette.themePrimary,
        warning: theme.palette.orange,
        danger: theme.palette.redDark,
    }[tone];

    const content = (
        <Stack
            as={onClick ? 'button' : 'div'}
            aria-pressed={onClick ? Boolean(isActive) : undefined}
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 6 }}
            styles={{
                root: {
                    padding: '4px 10px',
                    font: 'inherit',
                    borderRadius: '12px',
                    backgroundColor: isActive
                        ? theme.palette.themeLighter
                        : theme.palette.neutralLighter,
                    border: `1px solid ${
                        isActive
                            ? theme.palette.themePrimary
                            : theme.palette.neutralLight
                    }`,
                    cursor: onClick ? 'pointer' : 'default',
                    userSelect: 'none',
                },
            }}
            onClick={onClick}
        >
            <Text
                variant="small"
                styles={{ root: { fontWeight: 600, color: toneColor } }}
            >
                {value}
            </Text>
            <Text
                variant="small"
                styles={{ root: { color: theme.palette.neutralSecondary } }}
            >
                {label}
            </Text>
            {isActive && (
                <Icon iconName="Cancel" styles={{ root: { fontSize: 10 } }} />
            )}
        </Stack>
    );

    return tooltip ? (
        <TooltipHost content={tooltip}>{content}</TooltipHost>
    ) : (
        content
    );
};

/**
 * One-line headline counts. Replaces the four stacked summary cards the previous screen used,
 * which took most of the viewport before any actual data was visible.
 */
export const SreTriageSummary: React.FC<ISreTriageSummaryProps> = ({
    summary,
    theme,
    hasActiveFilter,
    activeAgeFilterHours,
    onFilterByAge,
}) => {
    const toggleAge = (hours: number) =>
        onFilterByAge(activeAgeFilterHours === hours ? undefined : hours);

    return (
        <Stack
            horizontal
            wrap
            tokens={{ childrenGap: 8 }}
            verticalAlign="center"
        >
            <Chip
                theme={theme}
                tone="primary"
                label={hasActiveFilter ? 'matching' : 'in-flight'}
                value={summary.matchingCount.toLocaleString()}
                tooltip="Rows matching the current filter"
            />
            {hasActiveFilter && (
                <Chip
                    theme={theme}
                    label="in-flight total"
                    value={summary.fleetInflightCount.toLocaleString()}
                    tooltip="Total in-flight, ignoring the current filter"
                />
            )}
            {summary.duplicateCount > 0 && (
                <Chip
                    theme={theme}
                    tone="warning"
                    label="duplicates"
                    value={summary.duplicateCount}
                    tooltip="Identifiers with more than one in-flight orchestration. This is a data integrity problem."
                />
            )}
            {summary.olderThanOneDayCount > 0 && (
                <Chip
                    theme={theme}
                    tone="warning"
                    label="> 1 day"
                    value={summary.olderThanOneDayCount}
                    tooltip="Filter to items older than 1 day"
                    isActive={activeAgeFilterHours === 24}
                    onClick={() => toggleAge(24)}
                />
            )}
            {summary.olderThanSixDaysCount > 0 && (
                <Chip
                    theme={theme}
                    tone="danger"
                    label="> 6 days"
                    value={summary.olderThanSixDaysCount}
                    tooltip="Filter to items older than 6 days"
                    isActive={activeAgeFilterHours === 144}
                    onClick={() => toggleAge(144)}
                />
            )}
        </Stack>
    );
};
